// ============================================================
// 📁 آدرس این فایل در پروژه:
//    src/pages/tracking/DistributionForm.tsx
// 
// این فایل را در همان مسیر بالا کپی کنید (روی فایل قبلی بنویسید)
// ============================================================

import { useState, useEffect, useCallback } from "react";
import { useTheme, DARK, LIGHT } from "../../components/Layout";
import { trackingAPI, warehouseAPI, customersAPI } from "../../services/api";
import { useSubmit } from "../../hooks/useSubmit";
import SearchableSelect from "../../components/SearchableSelect";
import PersianDatePicker, { todayGregorian } from "../../utils/PersianDatePicker";
import { ArrowRight, Save, Warehouse, User, AlertTriangle } from "lucide-react";

const fmt = (n: any) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 3 }).format(Number(n) || 0);

interface Props {
  item: any;       // اطلاعات جنس از shipment
  editing: any;    // اگر در حال ویرایش، اطلاعات توزیع
  onBack: () => void;
}

export default function DistributionForm({ item, editing, onBack }: Props) {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const { submitting, submit } = useSubmit();

  const isEdit = !!editing;

  const [destType, setDestType] = useState<"warehouse" | "customer">(
    editing?.destination_type || "warehouse"
  );
  const [warehouseId, setWarehouseId] = useState(editing?.warehouse_id ? String(editing.warehouse_id) : "");
  const [customerId, setCustomerId] = useState(editing?.customer_id ? String(editing.customer_id) : "");
  const [expected, setExpected] = useState(editing ? String(editing.expected_qty) : "");
  const [received, setReceived] = useState(editing ? String(editing.received_qty) : "");
  const [shortageReason, setShortageReason] = useState(editing?.shortage_reason || "");
  const [date, setDate] = useState(editing?.distribution_date || todayGregorian());
  const [notes, setNotes] = useState(editing?.notes || "");

  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ text: "", ok: true });

  const showToast = (text: string, ok = true) => {
    setToast({ text, ok });
    setTimeout(() => setToast({ text: "", ok: true }), 3000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    const [w, c] = await Promise.all([
      warehouseAPI.warehousesList(),
      customersAPI.list(),
    ]);
    if (w.success) setWarehouses(w.warehouses || []);
    if (c.success) setCustomers(c.customers || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // اگر مقدار اولیه تغییر کرد و رسیده خالی است، خودکار همان مقدار را در رسیده بگذار
  useEffect(() => {
    if (expected && !received) {
      setReceived(expected);
    }
  }, [expected, received]);

  const expectedNum = parseFloat(expected) || 0;
  const receivedNum = parseFloat(received) || 0;
  const shortage = Math.max(0, expectedNum - receivedNum);
  const remaining = isEdit
    ? (item.remaining_qty + (editing?.expected_qty || 0))  // در ویرایش، مقدار قبلی به باقی‌مانده برمی‌گردد
    : item.remaining_qty;

  const handleSave = async () => {
    if (expectedNum <= 0) { showToast("مقدار اولیه الزامی است", false); return; }
    if (expectedNum > remaining + 0.001) {
      showToast(`مقدار اولیه نمی‌تواند بیشتر از باقی‌مانده (${fmt(remaining)}) باشد`, false);
      return;
    }
    if (receivedNum < 0) { showToast("مقدار رسیده نمی‌تواند منفی باشد", false); return; }
    if (receivedNum > expectedNum) {
      showToast("مقدار رسیده نمی‌تواند بیشتر از مقدار اولیه باشد", false);
      return;
    }
    if (destType === "warehouse" && !warehouseId) { showToast("انتخاب گدام الزامی است", false); return; }
    if (destType === "customer" && !customerId) { showToast("انتخاب مشتری الزامی است", false); return; }

    const wh = warehouses.find((w: any) => String(w.id) === warehouseId);
    const cu = customers.find((c: any) => String(c.id) === customerId);

    const data = {
      shipment_item_id: item.id,
      destination_type: destType,
      warehouse_id: destType === "warehouse" ? parseInt(warehouseId) : null,
      warehouse_name: destType === "warehouse" ? (wh?.name || "") : null,
      customer_id: destType === "customer" ? parseInt(customerId) : null,
      customer_name: destType === "customer" ? `${cu?.first_name || ""} ${cu?.last_name || ""} ${cu?.company_name || ""}`.trim() : null,
      expected_qty: expectedNum,
      received_qty: receivedNum,
      shortage_reason: shortageReason,
      distribution_date: date,
      notes,
    };

    let res;
    if (isEdit) {
      res = await trackingAPI.updateDistribution({ id: editing.id, ...data });
    } else {
      res = await trackingAPI.addDistribution(data);
    }

    if (res.success) {
      showToast(isEdit ? "توزیع ویرایش شد" : "توزیع ثبت شد");
      setTimeout(onBack, 800);
    } else {
      showToast(res.message || "خطا", false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: "center", padding: 60, color: T.subtext }}>در حال بارگذاری...</div>;
  }

  const inp: React.CSSProperties = {
    width: "100%", padding: "10px 14px", borderRadius: 10,
    border: `1.5px solid ${T.cardBorder}`,
    background: dark ? "rgba(255,255,255,0.05)" : "#f8faff",
    color: T.text, fontSize: 13, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
    direction: "rtl", outline: "none", boxSizing: "border-box",
  };
  const lbl: React.CSSProperties = {
    fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5,
  };

  return (
    <div dir="rtl" style={{ fontFamily: "'Vazirmatn','Segoe UI',sans-serif", maxWidth: 800 }}>
      {toast.text && (
        <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 9999, padding: "12px 24px", borderRadius: 12, background: toast.ok ? "#10b981" : "#ef4444", color: "#fff", fontWeight: 700, fontSize: 13, boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}>
          {toast.text}
        </div>
      )}

      {/* هدر */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 10, border: `1px solid ${T.cardBorder}`, background: "transparent", color: T.subtext, cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>
          <ArrowRight size={15} /> بازگشت
        </button>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: T.text }}>
          {isEdit ? "ویرایش توزیع" : "ثبت توزیع جدید"}
        </h2>
      </div>

      {/* خلاصه جنس */}
      <div style={{ padding: 16, borderRadius: 12, background: dark ? "rgba(249,115,22,0.08)" : "#fff7ed", border: "1px solid rgba(249,115,22,0.2)", marginBottom: 18 }}>
        <div style={{ display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.subtext }}>جنس</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#f97316" }}>{item.product_name}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.subtext }}>واحد</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{item.unit || "—"}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.subtext }}>مقدار کل بار</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{fmt(item.quantity)}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.subtext }}>قبلاً توزیع شده</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#3b82f6" }}>{fmt(item.distributed_qty - (editing?.expected_qty || 0))}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.subtext }}>قابل توزیع</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#10b981" }}>{fmt(remaining)}</div>
          </div>
        </div>
      </div>

      {/* فرم */}
      <div style={{ padding: 22, borderRadius: 14, background: dark ? "rgba(255,255,255,0.03)" : "#fff", border: `1px solid ${T.cardBorder}` }}>

        {/* انتخاب نوع مقصد */}
        <div style={{ marginBottom: 18 }}>
          <label style={lbl}>مقصد *</label>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => setDestType("warehouse")} style={{
              flex: 1, padding: "14px", borderRadius: 11,
              border: `2px solid ${destType === "warehouse" ? "#10b981" : T.cardBorder}`,
              background: destType === "warehouse" ? "rgba(16,185,129,0.1)" : "transparent",
              color: destType === "warehouse" ? "#10b981" : T.subtext,
              cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              transition: "all 0.2s",
            }}>
              <Warehouse size={18} />
              تخلیه به گدام
            </button>
            <button onClick={() => setDestType("customer")} style={{
              flex: 1, padding: "14px", borderRadius: 11,
              border: `2px solid ${destType === "customer" ? "#8b5cf6" : T.cardBorder}`,
              background: destType === "customer" ? "rgba(139,92,246,0.1)" : "transparent",
              color: destType === "customer" ? "#8b5cf6" : T.subtext,
              cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              transition: "all 0.2s",
            }}>
              <User size={18} />
              فروش مستقیم به مشتری
            </button>
          </div>
        </div>

        {/* انتخاب گدام/مشتری */}
        <div style={{ marginBottom: 14 }}>
          {destType === "warehouse" ? (
            <>
              <label style={lbl}>گدام مقصد *</label>
              <SearchableSelect
                options={warehouses.map((w: any) => ({ value: String(w.id), label: w.name }))}
                value={warehouseId}
                onChange={setWarehouseId}
                placeholder="انتخاب گدام"
              />
            </>
          ) : (
            <>
              <label style={lbl}>مشتری *</label>
              <SearchableSelect
                options={customers.map((c: any) => ({ value: String(c.id), label: `${c.first_name || ""} ${c.last_name || ""} ${c.company_name || ""}`.trim() + (c.phone ? ` (${c.phone})` : "") }))}
                value={customerId}
                onChange={setCustomerId}
                placeholder="انتخاب مشتری"
              />
            </>
          )}
        </div>

        {/* مقدار اولیه و رسیده */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>
          <div>
            <label style={lbl}>مقدار اولیه (طبق سند) *</label>
            <input
              type="number"
              value={expected}
              onChange={e => setExpected(e.target.value)}
              placeholder="0"
              style={inp}
            />
            <div style={{ fontSize: 10, color: T.subtext, marginTop: 4 }}>چقدر باید برسد</div>
          </div>
          <div>
            <label style={lbl}>مقدار رسیده (واقعی) *</label>
            <input
              type="number"
              value={received}
              onChange={e => setReceived(e.target.value)}
              placeholder="0"
              style={{ ...inp, borderColor: shortage > 0 ? "#ef4444" : T.cardBorder }}
            />
            <div style={{ fontSize: 10, color: T.subtext, marginTop: 4 }}>چقدر واقعاً رسید</div>
          </div>
          <div>
            <label style={lbl}>کسری</label>
            <div style={{
              padding: "10px 14px", borderRadius: 10,
              background: shortage > 0 ? "rgba(239,68,68,0.1)" : (dark ? "rgba(16,185,129,0.08)" : "#f0fdf4"),
              color: shortage > 0 ? "#ef4444" : "#10b981",
              border: `1.5px solid ${shortage > 0 ? "rgba(239,68,68,0.3)" : "rgba(16,185,129,0.3)"}`,
              fontSize: 14, fontWeight: 800, minHeight: 40, display: "flex", alignItems: "center", gap: 6,
            }}>
              {shortage > 0 ? (
                <>
                  <AlertTriangle size={15} /> {fmt(shortage)} {item.unit || ""}
                </>
              ) : (
                <>✓ بدون کسری</>
              )}
            </div>
          </div>
        </div>

        {/* علت کسری - فقط اگر کسری وجود داشته باشد */}
        {shortage > 0 && (
          <div style={{ marginBottom: 14, padding: 12, borderRadius: 10, background: "rgba(239,68,68,0.05)", border: "1px solid rgba(239,68,68,0.2)" }}>
            <label style={{ ...lbl, color: "#ef4444" }}>علت کسری (اختیاری)</label>
            <input
              value={shortageReason}
              onChange={e => setShortageReason(e.target.value)}
              placeholder="مثلاً: سوراخ شدن کیسه‌ها، گم شدن در راه، ..."
              style={inp}
            />
          </div>
        )}

        {/* تاریخ و یادداشت */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 14 }}>
          <div>
            <label style={lbl}>تاریخ توزیع *</label>
            <PersianDatePicker value={date} onChange={setDate} />
          </div>
          <div>
            <label style={lbl}>یادداشت</label>
            <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="یادداشت..." style={inp} />
          </div>
        </div>
      </div>

      {/* راهنمای کوتاه */}
      {destType === "warehouse" && (
        <div style={{ marginTop: 14, padding: 12, borderRadius: 10, background: "rgba(16,185,129,0.05)", border: "1px solid rgba(16,185,129,0.2)", fontSize: 11, color: T.subtext, lineHeight: 1.7 }}>
          💡 وقتی این توزیع را ثبت کنید، <strong style={{ color: "#10b981" }}>{fmt(receivedNum)}</strong> {item.unit || ""} از {item.product_name} به موجودی گدام اضافه می‌شود (نه مقدار اولیه - بلکه فقط مقدار رسیده).
        </div>
      )}

      {/* دکمه‌ها */}
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 24 }}>
        <button onClick={onBack} style={{ padding: "11px 24px", borderRadius: 11, border: `1.5px solid ${T.cardBorder}`, background: "transparent", color: T.text, cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit" }}>
          انصراف
        </button>
        <button onClick={() => submit(handleSave)} disabled={submitting} style={{
          display: "flex", alignItems: "center", gap: 7,
          padding: "11px 28px", borderRadius: 11, border: "none",
          background: submitting ? "#94a3b8" : "linear-gradient(135deg,#10b981,#059669)",
          color: "#fff", cursor: submitting ? "not-allowed" : "pointer",
          fontSize: 13, fontWeight: 800, fontFamily: "inherit",
          boxShadow: submitting ? "none" : "0 4px 14px rgba(16,185,129,0.4)",
        }}>
          <Save size={15} />
          {submitting ? "در حال ذخیره..." : (isEdit ? "ذخیره تغییرات" : "ثبت توزیع")}
        </button>
      </div>
    </div>
  );
}